import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TestimonialCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const testimonials = [
  {
    id: 1,
    type: "client",
    name: "Sarah ",
    role: "CEO",
    company: "GreenConnect",
    image: "", // You can add an image URL here if available
    content: `Adrian's UI/UX design work for GreenConnect was a game-changer. His ability to blend aesthetics with intuitive functionality led to a 200% increase in user engagement within the first quarter. He's a true professional who understands both design and user behavior.`,
    rating: 5,
    project: "GreenConnect UI/UX Revamp",
    metrics: {
      label: "User Engagement",
      value: "200%"
    },
    badge: "Client Success"
  },
  {
    id: 2,
    type: "client",
    name: "Mr Quincy",
    role: "Project Manager",
    company: "Final Touch Construction",
    image: "",
    content: `Working with Adrian on our new project management tool was a fantastic experience. His technical skills streamlined our workflows, resulting in a 50% reduction in project delays. He's an expert at building robust solutions that meet real-world business needs.`,
    rating: 5,
    project: "Project Management Tool",
    metrics: {
      label: "Project Efficiency",
      value: "50% reduction in delays"
    },
    badge: "Client Success"
  },
];

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials?.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials?.length) % testimonials?.length);
  };

  const goToTestimonial = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(nextTestimonial, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const getBadgeColor = (type) => {
    switch (type) {
      case 'client':
        return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
      case 'mentee':
        return 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20';
      case 'peer':
        return 'bg-purple-500/10 text-purple-600 border-purple-500/20';
      default:
        return 'bg-primary/10 text-primary border-primary/20';
    }
  };

  const currentTestimonial = testimonials?.[currentIndex];

  return (
    <section className="py-20 bg-muted/30">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Icon name="MessageSquare" size={20} className="text-primary" />
            <span className="text-sm font-brand-medium text-primary tracking-wide uppercase">Testimonials</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-brand-headline text-foreground mb-6">
            What People Say About
            <span className="block text-primary text-gradient-gold">Working With Me</span>
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto font-brand-body">
            Real feedback from clients, mentees, and industry peers who have experienced 
            the impact of collaborative growth and technical excellence.
          </p>
        </div>

        {/* Testimonial Carousel */}
        <div className="relative">
          {/* Main Testimonial Card */}
          <div className="bg-card rounded-2xl p-8 lg:p-12 border border-border brand-shadow-lg">
            {/* Badge */}
            <div className="flex items-center justify-between mb-6">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-brand-medium border ${getBadgeColor(currentTestimonial?.type)}`}>
                <Icon 
                  name={currentTestimonial?.type === 'client' ? 'Briefcase' : currentTestimonial?.type === 'mentee' ? 'GraduationCap' : 'Users'} 
                  size={14} 
                  className="mr-1" 
                />
                {currentTestimonial?.badge}
              </span>
              
              {/* Rating */}
              <div className="flex items-center space-x-1">
                {Array.from({ length: 5 }, (_, i) => (
                  <Icon
                    key={i}
                    name="Star"
                    size={16}
                    className={`${i < currentTestimonial?.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                  />
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="grid lg:grid-cols-3 gap-8 items-center">
              {/* Testimonial Text */}
              <div className="lg:col-span-2">
                <blockquote className="text-lg lg:text-xl text-foreground font-brand-body leading-relaxed mb-6">
                  "{currentTestimonial?.content}"
                </blockquote>
                
                {/* Project & Metrics */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                  <div>
                    <div className="text-sm text-text-secondary font-brand-body">
                      <span className="font-brand-medium">Project:</span> {currentTestimonial?.project}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-lg border border-primary/20">
                    <Icon name="TrendingUp" size={16} className="text-primary" />
                    <span className="text-sm font-brand-medium text-primary">
                      {currentTestimonial?.metrics?.label}: {currentTestimonial?.metrics?.value}
                    </span>
                  </div>
                </div>
              </div>

              {/* Author Info */}
              <div className="text-center lg:text-left">
                <div className="inline-block relative mb-4">
                  <div className="w-20 h-20 mx-auto lg:mx-0 rounded-full overflow-hidden border-4 border-primary/20">
                    <Image
                      src={currentTestimonial?.image}
                      alt={currentTestimonial?.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                    <Icon name="Check" size={14} className="text-primary-foreground" />
                  </div>
                </div>
                
                <div>
                  <h4 className="text-lg font-brand-subheading text-foreground mb-1">
                    {currentTestimonial?.name}
                  </h4>
                  <p className="text-sm text-primary font-brand-medium mb-1">
                    {currentTestimonial?.role}
                  </p>
                  <p className="text-sm text-text-secondary font-brand-body">
                    {currentTestimonial?.company}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8">
            <button
              onClick={prevTestimonial}
              className="p-3 bg-background border border-border rounded-full hover:border-primary/30 hover:bg-primary/5 transition-all duration-300 brand-shadow"
              aria-label="Previous testimonial"
            >
              <Icon name="ChevronLeft" size={20} className="text-text-secondary hover:text-primary" />
            </button>

            {/* Dots Indicator */}
            <div className="flex items-center space-x-2">
              {testimonials?.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? 'bg-primary scale-125' :'bg-border hover:bg-primary/30'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>

            <button
              onClick={nextTestimonial}
              className="p-3 bg-background border border-border rounded-full hover:border-primary/30 hover:bg-primary/5 transition-all duration-300 brand-shadow"
              aria-label="Next testimonial"
            >
              <Icon name="ChevronRight" size={20} className="text-text-secondary hover:text-primary" />
            </button>
          </div>

          {/* Auto-play Toggle */}
          <div className="text-center mt-6">
            <button
              onClick={() => setIsAutoPlaying(!isAutoPlaying)}
              className="inline-flex items-center space-x-2 text-sm text-text-secondary hover:text-primary transition-colors"
            >
              <Icon name={isAutoPlaying ? "Pause" : "Play"} size={16} />
              <span>{isAutoPlaying ? "Pause" : "Play"} Auto-rotation</span>
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-16 border-t border-border">
          <div className="text-center">
            <div className="text-2xl font-brand-headline text-primary mb-1">50+</div>
            <div className="text-sm text-text-secondary font-brand-body">Happy Clients</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-brand-headline text-primary mb-1">234+</div>
            <div className="text-sm text-text-secondary font-brand-body">Mentees Guided</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-brand-headline text-primary mb-1">15K+</div>
            <div className="text-sm text-text-secondary font-brand-body">Community Members</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-brand-headline text-primary mb-1">98%</div>
            <div className="text-sm text-text-secondary font-brand-body">Satisfaction Rate</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialCarousel;